import React, { useContext, useState } from "react";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import toMovieUrl from "@functions/toMovieUrl";
import useViewport from "@hooks/useViewport";
import { ApplicationContext } from "@context/ApplicationContext";
import { SearchContext } from "@context/SearchContext";
import PlayIcon from "@components/Icons/PlayIcon";
import TicketIcon from "@components/Icons/TicketIcon";
import TrailerButton from "@components/TrailerButton";
import AddToWatchListButton from "@components/V3/AddToWatchListButton";
import { getImageAtSize } from "@utils/imageUtils";
import "./style.scss";

const MovieTile = ({ movie, variant = "now-showing", onRemovedFromWatchlist, initialInWatchlist }) => {
    const { whiteLabel } = useContext(ApplicationContext);
    const { venueName } = useContext(SearchContext);
    const { width } = useViewport();
    const blurbBreakpoint = 640;
    const config = getAppConfig();
    const [isActive, setIsActive] = useState(false);
    const hideWatchlist = whiteLabel?.hide_navigation_menu === true || whiteLabel?.hide_profile_menu === true;
    const fallbackImage = `${config?.cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;
    const isComingSoon = variant === "coming-soon";



    const movieData = {
        id: isComingSoon ? movie.id : movie.movie_id,
        title: isComingSoon ? movie.title : movie.movie_title,
        image: isComingSoon ? movie.movie_image?.[0]?.original_url : movie.movie_image,
        rating: movie.rating,
        runningTime: movie.running_time,
        genre: isComingSoon ? movie.bo_genres?.[0]?.genre_name : movie.genre_name,
        description: isComingSoon ? movie.bo_reviews?.[0]?.review_description : movie.movie_blurb,
        trailerUrl: movie.trailer_url,
        releaseDate: movie.release_date,
        releaseYear: movie.release_year,
        hasSessions: isComingSoon ? movie.ts_sessions && movie.ts_sessions.length > 0 : true
    };

    const onImageError = (e) => {
        e.target.src = fallbackImage;
    };

    const handleClick = () => {
        setIsActive(!isActive);
    };

    const handleWatchlistToggle = (inWatchlist, _action) => {
        if (!inWatchlist && onRemovedFromWatchlist) {
            onRemovedFromWatchlist(movieData.id);
        }
    };

    const formatReleaseDate = (dateString) => {
        if (!dateString) return "Release date to be announced";

        try {
            const date = parseISO(dateString);
            return format(date, "dd MMM yyyy");
        } catch {
            return "Release date to be announced";
        }
    };

    const formattedDate = formatReleaseDate(movieData.releaseDate);

    return (
        <div className={`movie-tile movie-tile--${variant}`}>
            <Link
                className="movie-tile__poster-link"
                to={toMovieUrl(movieData.id, movieData.title, venueName)}
            >
                <img
                    className="movie-tile__image"
                    src={getImageAtSize(movieData.image, 428) || fallbackImage}
                    alt={movieData.title}
                    onError={onImageError}
                />
            </Link>
            <div className="movie-tile__content">
                <Link
                    className="movie-tile__title"
                    to={toMovieUrl(movieData.id, movieData.title, venueName)}
                >
                    {movieData.title}
                </Link>
                {isComingSoon && movieData.releaseDate && (
                    <div className="movie-tile__release">{formattedDate}</div>
                )}
                <ul className="movie-tile__meta">
                    {movieData.rating && (
                        <li
                            className={`movie-tile__rating movie-tile__rating--${movieData.rating.replace("+", "")}`}
                            role="status"
                            aria-label="Movie Rating"
                        >
                            {movieData.rating}
                        </li>
                    )}
                    {movieData.runningTime && (
                        <li className="movie-tile__info">
                            {movieData.runningTime}mins
                        </li>
                    )}
                    {!isComingSoon && movieData.releaseYear && (
                        <li className="movie-tile__info">
                            {movieData.releaseYear}
                        </li>
                    )}
                    {movieData.genre && (
                        <li className="movie-tile__info">
                            {movieData.genre}
                        </li>
                    )}
                </ul>
                {width >= blurbBreakpoint && movieData.description && (
                    <button
                        className={`movie-tile__blurb ${isActive ? "movie-tile__blurb--active" : ""}`}
                        onClick={handleClick}
                    >
                        {movieData.description}
                    </button>
                )}
                <ul className="movie-tile__btns">
                    {movieData.hasSessions && (
                        <li className="movie-tile__btn-item">
                            <Link
                                className="movie-tile__btn movie-tile__btn--book"
                                to={toMovieUrl(movieData.id, movieData.title, venueName)}
                            >
                                <TicketIcon className="movie-tile__btn-icon" />
                                <span className="movie-tile__btn-text">
                                    Book Now
                                </span>
                            </Link>
                        </li>

                    )}
                    {movieData.trailerUrl && (
                        <li className="movie-tile__btn-item">
                            <TrailerButton
                                extraClass="movie-tile__btn movie-tile__btn--trailer"
                                type="mp4"
                                trailerLink={movieData.trailerUrl}
                            >
                                <PlayIcon className="movie-tile__btn-icon" />
                                <span className="movie-tile__btn-text">
                                    Trailer
                                </span>
                            </TrailerButton>
                        </li>
                    )}
                    {!hideWatchlist && (
                        <li className="movie-tile__btn-item">
                            <AddToWatchListButton
                                movieId={movieData.id}
                                initialInWatchlist={initialInWatchlist}
                                onToggle={handleWatchlistToggle}
                            />
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default MovieTile;
